import { useEffect, useMemo, useState } from "react";
import Editor, { DiffEditor } from "@monaco-editor/react";
import { t } from "../../i18n";
import { api } from "../../api";
import { languageForPath } from "../editor/fileLanguage";
import type {
  DevelopmentSelection,
  FileViewMode,
  Result,
} from "../chat/ChatDevelopmentPanel.types";
import { selectionBody } from "../chat/ChatDevelopmentPanel.types";

export function FileTab({
  conversationId,
  selection,
  path,
  initialMode = "inline",
}: {
  conversationId: string;
  selection: DevelopmentSelection;
  path: string;
  initialMode?: FileViewMode;
}) {
  const [mode, setMode] = useState<FileViewMode>(initialMode);
  const [current, setCurrent] = useState("");
  const [original, setOriginal] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editorReady, setEditorReady] = useState(false);
  const needsOriginal = mode !== "file";
  const language = useMemo(() => languageForPath(path), [path]);
  useEffect(() => {
    let active = true;
    void import("../editor/monacoSetup").then(() => {
      if (active) setEditorReady(true);
    });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    setCurrent("");
    setOriginal("");
    setLoading(true);
    const body = JSON.stringify({ ...selectionBody(selection), path });
    const loadCurrent = api<Result>(
      `/conversations/${conversationId}/development-workspace/git/file`,
      {
        method: "POST",
        body,
        signal: controller.signal,
        notifyDataChange: false,
      },
    )
      .then((next) => {
        if (next.code !== 0)
          throw new Error(next.stderr.trim() || "Could not load file");
        setCurrent(next.stdout);
      })
      .catch((reason) => {
        if (controller.signal.aborted) return;
        setError(
          reason instanceof Error ? reason.message : "Could not load file",
        );
      });
    const loadOriginal = needsOriginal
      ? api<Result>(
          `/conversations/${conversationId}/development-workspace/git/original`,
          {
            method: "POST",
            body,
            signal: controller.signal,
            notifyDataChange: false,
          },
        )
          .then((previous) => {
            if (previous.code !== 0)
              throw new Error(
                previous.stderr.trim() || "Could not load the original file",
              );
            setOriginal(previous.stdout);
          })
          .catch((reason) => {
            if (!controller.signal.aborted)
              setError(
                reason instanceof Error
                  ? reason.message
                  : "Could not load the original file",
              );
          })
      : Promise.resolve();
    void Promise.all([loadCurrent, loadOriginal]).then(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [conversationId, path, selection, needsOriginal]);
  const options = {
    automaticLayout: true,
    readOnly: true,
    domReadOnly: true,
    minimap: { enabled: false },
    fontSize: 12,
    scrollBeyondLastLine: false,
    scrollBeyondLastColumn: 0,
    smoothScrolling: false,
    wordWrap: "off" as const,
    largeFileOptimizations: true,
    renderLineHighlight: "none" as const,
    renderOverviewRuler: false,
    scrollbar: {
      alwaysConsumeMouseWheel: false,
      verticalScrollbarSize: 10,
      horizontalScrollbarSize: 10,
    },
  };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-stone-200 px-3 dark:border-white/10">
        <span className="mr-auto truncate text-xs text-stone-500" title={path}>
          {path}
        </span>
        {(["file", "inline", "split"] as FileViewMode[]).map((value) => (
          <button
            key={value}
            className={`rounded-md px-2 py-1 text-[11px] capitalize ${mode === value ? "bg-lumen-100 text-lumen-800 dark:bg-lumen-900/40 dark:text-lumen-200" : "text-stone-500 hover:bg-stone-100 dark:hover:bg-white/5"}`}
            onClick={() => setMode(value)}
          >
            {value === "file"
              ? t("New version")
              : value === "split"
                ? t("Side by side")
                : t("Inline")}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1">
        {error ? (
          <pre className="p-4 text-xs text-red-600">{error}</pre>
        ) : loading || !editorReady ? (
          <div className="grid h-full place-items-center text-xs text-stone-400">
            {t("Loading…")}
          </div>
        ) : mode === "file" ? (
          <Editor
            language={language}
            value={current}
            options={options}
            theme={
              document.documentElement.classList.contains("dark")
                ? "vs-dark"
                : "light"
            }
          />
        ) : (
          <DiffEditor
            original={original}
            modified={current}
            originalLanguage={language}
            modifiedLanguage={language}
            options={{
              ...options,
              diffAlgorithm: "legacy",
              maxComputationTime: 750,
              renderSideBySide: mode === "split",
              renderIndicators: true,
              renderMarginRevertIcon: false,
            }}
            theme={
              document.documentElement.classList.contains("dark")
                ? "vs-dark"
                : "light"
            }
          />
        )}
      </div>
    </div>
  );
}
